import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Download, Search, SlidersHorizontal, Star, Users } from "lucide-react";
import { toast } from "sonner";
import { Chip, Empty, Modal, Page, Spinner } from "@/components/kit";
import ContactRow from "@/components/ContactRow";
import ContactsTable from "@/components/ContactsTable";
import ContactsDesktopToolbar from "@/components/ContactsDesktopToolbar";
import { useCategories, useContacts } from "@/hooks/queries";
import { exportCsv } from "@/lib/toto";

export const SORTS = [
  { key: "recent", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "name", label: "Name A–Z" },
  { key: "name_desc", label: "Name Z–A" },
];

export default function Contacts() {
  const [params, setParams] = useSearchParams();
  const categoryId = params.get("categoryId") || null;
  const [search, setSearch] = useState(params.get("q") || "");
  useEffect(() => { if (params.get("q") !== null) setSearch(params.get("q")); }, [params]);
  const [favorite, setFavorite] = useState(false);
  const [sort, setSort] = useState("recent");
  const [showSort, setShowSort] = useState(false);
  const cats = useCategories().data ?? [];
  const contacts = useContacts({ search: search.trim(), categoryId, favorite, sort });
  const list = contacts.data ?? [];
  const active = cats.find((c) => c.id === categoryId)?.name;
  const setCat = (id) => setParams(id ? { categoryId: id } : {});
  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.name]));
  const doExport = () => (list.length ? (exportCsv(list, catMap), toast.success(`Exported ${list.length} contacts ✓`)) : toast("No contacts to export."));

  return (
    <Page className="pt-4 lg:pt-8">
      <ContactsDesktopToolbar total={list.length} search={search} setSearch={setSearch} categoryId={categoryId} setCat={setCat} favorite={favorite} setFavorite={setFavorite} sort={sort} setSort={setSort} sorts={SORTS} onExport={doExport} />
      <div className="lg:hidden">
      <div className="flex items-center justify-between">
        <h1 className="text-[30px] lg:text-4xl font-extrabold">Contacts</h1>
        <div className="flex gap-2">
          <button className="t-iconbtn hidden sm:inline-flex" title="Export CSV" data-testid="contacts-export" onClick={doExport}><Download className="w-5 h-5" /></button>
          <button className={`t-iconbtn ${favorite ? "bg-[#FEF9C3] border-[#FDE68A]" : ""}`} onClick={() => setFavorite(!favorite)} data-testid="contacts-favorite-filter" title="Favorites"><Star className={`w-5 h-5 ${favorite ? "fill-[#D97706] text-[#D97706]" : ""}`} /></button>
          <button className="t-iconbtn" onClick={() => setShowSort(true)} data-testid="contacts-sort" title="Sort"><SlidersHorizontal className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="relative mt-4">
        <Search className="absolute left-4 top-[15px] w-5 h-5 text-[#475569]" />
        <input className="t-input pl-12" placeholder="Search contacts" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contacts-search" />
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-4 -mx-5 px-5 lg:mx-0 lg:px-0 lg:flex-wrap">
        <Chip selected={!categoryId} onClick={() => setCat(null)} testid="contacts-chip-all">All</Chip>
        {cats.map((c) => <Chip key={c.id} selected={categoryId === c.id} onClick={() => setCat(c.id)} testid={`contacts-chip-${c.slug}`}>{c.name}</Chip>)}
      </div>
      <div className="flex justify-between items-center mt-4 pb-3 border-b border-[#E2E8F0] -mx-5 px-5 lg:mx-0 lg:px-0">
        <span className="text-[14px] font-semibold text-[#475569]" data-testid="contacts-active-filter">{active || "All categories"}{favorite ? " · Favorites" : ""}</span>
        <span className="text-[13px] text-[#475569]">{SORTS.find((s) => s.key === sort).label}</span>
      </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-3 lg:hidden" data-testid="contacts-list">
        {contacts.isLoading && <Spinner />}
        {list.map((c) => <ContactRow key={c.id} contact={c} />)}
      </div>
      <div className="hidden lg:block mt-5">
        {contacts.isLoading ? <Spinner /> : list.length > 0 && <ContactsTable contacts={list} catMap={catMap} />}
      </div>
      {contacts.isSuccess && !list.length && (
        <Empty icon={Users} title={search || categoryId || favorite ? "No matching contacts" : "No contacts yet"} text={search || categoryId || favorite ? "Try a different search or filter." : "Scan your first visiting card to get started."}
          action={<Link to="/scan" className="t-btn t-btn-primary">Scan a Card</Link>} testid="contacts-empty" />
      )}
      <Modal open={showSort} onClose={() => setShowSort(false)} title="Sort contacts" testid="contacts-sort-sheet">
        <div className="flex flex-col">
          {SORTS.map((s) => (
            <button key={s.key} className="flex items-center justify-between h-12 px-2 rounded-xl hover:bg-[#F8FAFC] text-[15px] font-medium" onClick={() => { setSort(s.key); setShowSort(false); }} data-testid={`sort-${s.key}`}>
              {s.label}{sort === s.key && <Check className="w-5 h-5 text-[#0000EC]" />}
            </button>
          ))}
        </div>
      </Modal>
    </Page>
  );
}
